import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useCreateAiSessionMutation } from "../../services/users/ai";
import { streamPost } from "../../utils/aiSse";
import { toastAiError } from "../../utils/aiErrors";

const GlobalAssistantWidget = () => {
  const { t } = useTranslation("ai");
  const [open, setOpen] = useState(false);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [incomplete, setIncomplete] = useState(false);
  const [lastMessage, setLastMessage] = useState(null);
  const abortRef = useRef(null);
  const messagesRef = useRef(null);

  const [createSession, { isLoading: creating }] = useCreateAiSessionMutation();

  useEffect(() => {
    if (!messages.length) return;
    const el = messagesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, streaming]);

  useEffect(() => {
    return () => abortRef.current?.abort();
  }, []);

  const ensureSession = async () => {
    if (activeSessionId) return activeSessionId;
    const result = await createSession();
    if (result.error) {
      toastAiError(result.error, t);
      return null;
    }
    const session = result.data?.data || result.data;
    const id = session?.id;
    if (id) {
      setActiveSessionId(id);
      setMessages([]);
    }
    return id;
  };

  const handleNewChat = async () => {
    abortRef.current?.abort();
    setStreaming(false);
    setIncomplete(false);
    setLastMessage(null);
    const result = await createSession();
    if (result.error) {
      toastAiError(result.error, t);
      return;
    }
    const session = result.data?.data || result.data;
    setActiveSessionId(session?.id ?? null);
    setMessages([]);
  };

  const send = async (overrideText) => {
    const text = (overrideText ?? input).trim();
    if (!text || streaming) return;
    if (text.length > 2000) {
      toastAiError({ status: 422, data: { error: t("errors.messageTooLong") } }, t);
      return;
    }

    const sessionId = await ensureSession();
    if (!sessionId) return;

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLastMessage(text);
    setIncomplete(false);
    setInput("");
    setMessages((prev) => [
      ...prev,
      { role: "user", content: text },
      { role: "assistant", content: "", pending: true },
    ]);
    setStreaming(true);

    try {
      await streamPost(
        `/api/v1/user/ai/sessions/${sessionId}/messages`,
        { message: text },
        {
          signal: controller.signal,
          onToken: (token) => {
            setMessages((prev) => {
              const next = [...prev];
              const last = next[next.length - 1];
              if (last?.role === "assistant") {
                next[next.length - 1] = {
                  ...last,
                  content: (last.content || "") + token,
                  pending: true,
                };
              }
              return next;
            });
          },
        },
      );

      setMessages((prev) => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.role === "assistant") {
          next[next.length - 1] = { ...last, pending: false };
        }
        return next;
      });
      setStreaming(false);
    } catch (err) {
      if (controller.signal.aborted) return;
      setStreaming(false);
      if (err.incomplete) {
        setIncomplete(true);
        setMessages((prev) => {
          const next = [...prev];
          const last = next[next.length - 1];
          if (last?.role === "assistant") {
            next[next.length - 1] = { ...last, pending: false };
          }
          return next;
        });
        return;
      }
      toastAiError(err, t);
      setMessages((prev) => prev.slice(0, -1));
    }
  };

  return (
    <div className={`global-ai-assistant ${open ? "is-open" : ""}`}>
      {open && (
        <div className="global-ai-panel" role="dialog" aria-label={t("global.title")}>
          <div className="global-ai-panel__header">
            <strong>{t("global.title")}</strong>
            <div className="global-ai-panel__header-actions">
              <button
                type="button"
                className="btn btn-sm btn-main-color-border font-15"
                onClick={handleNewChat}
                disabled={creating || streaming}
              >
                {t("global.newChat")}
              </button>
              <button
                type="button"
                className="btn btn-sm btn-light global-ai-close-btn"
                onClick={() => setOpen(false)}
                aria-label={t("global.close")}
              >
                <i className="fas fa-times" aria-hidden="true" />
              </button>
            </div>
          </div>

          <div className="global-ai-panel__body">
            <div className="global-ai-chat">
              <div className="ai-chat-messages" ref={messagesRef}>
                {!messages.length && (
                  <p className="ai-muted">{t("global.emptySession")}</p>
                )}
                {messages.map((msg, i) => (
                  <div
                    key={`${msg.role}-${i}`}
                    className={`ai-chat-bubble ai-chat-bubble--${msg.role}`}
                  >
                    <pre className="ai-pre">
                      {msg.content || (msg.pending ? "…" : "")}
                    </pre>
                  </div>
                ))}
              </div>

              {incomplete && (
                <div className="ai-retry-row">
                  <p className="ai-muted mb-1">{t("global.incomplete")}</p>
                  <button
                    type="button"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => lastMessage && send(lastMessage)}
                  >
                    {t("global.retry")}
                  </button>
                </div>
              )}

              <form
                className="ai-chat-form"
                onSubmit={(e) => {
                  e.preventDefault();
                  send();
                }}
              >
                <input
                  type="text"
                  className="form-control"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  placeholder={t("global.placeholder")}
                  maxLength={2000}
                  disabled={streaming}
                />
                <button
                  type="submit"
                  className="btn btn-main-color"
                  disabled={streaming || !input.trim()}
                >
                  {t("global.send")}
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      <button
        type="button"
        className="global-ai-fab"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? t("global.close") : t("global.open")}
      >
        {open ? "×" : <i className="fas fa-robot" aria-hidden="true" />}
      </button>
    </div>
  );
};

export default GlobalAssistantWidget;
